/**
 * DAG Object Validation Module
 *
 * Provides validation for DAG objects at entry points (P2P sync, store operations).
 * Uses existing cryptographic primitives (verifyHash, verifySignature).
 */

import { verifyHash } from '../dag/object.js';
import { verifySignature } from '../identity/signing.js';
import { base58 } from '../identity/base58.js';
import type { DAGObject } from '../dag/types.js';

// Constants
export const MIN_VALID_TIMESTAMP = 1700000000000; // Nov 2023 - before AOS existed
export const MAX_FUTURE_DRIFT_MS = 3600000; // 1 hour - allow for clock skew

// Server-generated objects use this special signature
export const SERVER_GENERATED_SIGNATURE = 'server-generated';

/**
 * Validation result with list of errors
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Options for validateDAGObject
 */
export interface ValidationOptions {
  /** Verify object hash matches content (default: true) */
  verifyHash?: boolean;
  /** Verify Ed25519 signature (default: true) */
  verifySignature?: boolean;
  /** Verify timestamp is within valid range (default: true) */
  verifyTimestamp?: boolean;
  /** Verify pubkey is valid base58/32-byte (default: true) */
  verifyPubkey?: boolean;
  /** Allow 'server-generated' as valid signature for CGI objects (default: false) */
  allowServerSigned?: boolean;
}

const DEFAULT_OPTIONS: Required<ValidationOptions> = {
  verifyHash: true,
  verifySignature: true,
  verifyTimestamp: true,
  verifyPubkey: true,
  allowServerSigned: false,
};

/**
 * Validate a public key string
 * - Must be valid base58
 * - Must decode to exactly 32 bytes (Ed25519 public key length)
 */
export function validatePubkey(pubkey: string): { valid: boolean; reason?: string } {
  if (typeof pubkey !== 'string' || pubkey.length === 0) {
    return { valid: false, reason: 'pubkey must be a non-empty string' };
  }

  try {
    const decoded = base58.decode(pubkey);
    if (decoded.length !== 32) {
      return { valid: false, reason: `pubkey must be 32 bytes, got ${decoded.length}` };
    }
    return { valid: true };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'unknown error';
    return { valid: false, reason: `invalid base58: ${message}` };
  }
}

/**
 * Validate a timestamp
 * - Must be >= MIN_VALID_TIMESTAMP (Nov 2023)
 * - Must be <= now + MAX_FUTURE_DRIFT_MS (1 hour into future)
 */
export function validateTimestamp(timestamp: number): { valid: boolean; reason?: string } {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) {
    return { valid: false, reason: 'timestamp must be a finite number' };
  }

  if (timestamp < MIN_VALID_TIMESTAMP) {
    return { valid: false, reason: `timestamp too old (before Nov 2023): ${timestamp}` };
  }

  const maxFuture = Date.now() + MAX_FUTURE_DRIFT_MS;
  if (timestamp > maxFuture) {
    return { valid: false, reason: `timestamp too far in future: ${timestamp} > ${maxFuture}` };
  }

  return { valid: true };
}

/**
 * Check basic structural validity of a DAG object
 */
function validateStructure(obj: unknown): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (obj === null || typeof obj !== 'object') {
    return { valid: false, errors: ['object must be a non-null object'] };
  }

  const dag = obj as Record<string, unknown>;

  // Required string fields
  const requiredStrings = ['hash', 'type', 'creator', 'signature'];
  for (const field of requiredStrings) {
    if (typeof dag[field] !== 'string' || (dag[field] as string).length === 0) {
      errors.push(`${field} must be a non-empty string`);
    }
  }

  // Required number field
  if (typeof dag.timestamp !== 'number') {
    errors.push('timestamp must be a number');
  }

  // Required array field
  if (!Array.isArray(dag.parents)) {
    errors.push('parents must be an array');
  }

  // Content must exist
  if (dag.content === undefined || dag.content === null) {
    errors.push('content is required');
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate a DAG object for storage
 *
 * Checks:
 * - Object structure (required fields, types)
 * - Hash integrity (hash matches content)
 * - Signature validity (Ed25519 signature from creator)
 * - Timestamp range (Nov 2023 to now + 1hr)
 * - Public key format (valid base58, 32 bytes)
 *
 * @param obj - The object to validate (unknown type for safe parsing)
 * @param options - Validation options
 * @returns ValidationResult with valid flag and list of errors
 */
export function validateDAGObject(
  obj: unknown,
  options: ValidationOptions = {}
): ValidationResult {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const errors: string[] = [];

  // Check structure first
  const structureResult = validateStructure(obj);
  if (!structureResult.valid) {
    return { valid: false, errors: structureResult.errors };
  }

  const dagObj = obj as DAGObject;

  // Validate pubkey format
  if (opts.verifyPubkey) {
    const pubkeyResult = validatePubkey(dagObj.creator);
    if (!pubkeyResult.valid) {
      errors.push(`invalid creator pubkey: ${pubkeyResult.reason}`);
    }
  }

  // Validate timestamp range
  if (opts.verifyTimestamp) {
    const timestampResult = validateTimestamp(dagObj.timestamp);
    if (!timestampResult.valid) {
      errors.push(`invalid timestamp: ${timestampResult.reason}`);
    }
  }

  // Validate hash
  if (opts.verifyHash) {
    if (!verifyHash(dagObj)) {
      errors.push('hash does not match object content');
    }
  }

  // Validate signature
  if (opts.verifySignature) {
    // Check for server-generated signature
    if (dagObj.signature === SERVER_GENERATED_SIGNATURE) {
      if (!opts.allowServerSigned) {
        errors.push('server-generated signatures not allowed');
      }
      // If allowServerSigned is true, skip Ed25519 verification
    } else {
      // Standard Ed25519 verification
      if (!verifySignature(dagObj)) {
        errors.push('signature verification failed');
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Custom error for validation failures
 */
export class ValidationError extends Error {
  public readonly errors: string[];

  constructor(message: string, errors: string[] = []) {
    super(message);
    this.name = 'ValidationError';
    this.errors = errors;
  }
}
